import React, { useState } from 'react';
import { Car, FileText, ClipboardList, Activity, Salad, Globe, ExternalLink, CalendarDays } from 'lucide-react';
import FinSheet from './FinSheet';

const MONTHS = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];
const MONTH_NUMS = { JAN: 1, FEB: 2, MAR: 3, APR: 4, MAY: 5, JUN: 6, JUL: 7, AUG: 8, SEP: 9, OCT: 10, NOV: 11, DEC: 12 };
const YEARS_CHECKLIST = ['25', '26', '27'];
const YEARS_KM = ['2024', '2025', '2026', '2027'];

const TABS = [
  { key: 'doc',       label: 'Document Index',     icon: FileText,     accent: 'stone'   },
  { key: 'vehicle',   label: 'Vehicle Index',       icon: Car,          accent: 'emerald' },
  { key: 'checklist', label: 'Report Checklist',    icon: ClipboardList, accent: 'blue'   },
  { key: 'km',        label: 'Kilometer Log',       icon: Activity,     accent: 'rose'    },
  { key: 'diet',      label: 'Diet Chart',          icon: Salad,        accent: 'green'   },
  { key: 'govt',      label: 'Govt. Vehicle Site',  icon: Globe,        accent: 'indigo'  },
];

const TAB_COLORS = {
  stone:  { active: 'bg-stone-800 text-white',   hdr: 'bg-stone-50 border-stone-200',   btn: 'bg-stone-800 hover:bg-stone-900' },
  emerald:{ active: 'bg-emerald-700 text-white',  hdr: 'bg-emerald-50 border-emerald-200', btn: 'bg-emerald-700 hover:bg-emerald-800' },
  blue:   { active: 'bg-blue-700 text-white',     hdr: 'bg-blue-50 border-blue-200',    btn: 'bg-blue-700 hover:bg-blue-800' },
  rose:   { active: 'bg-rose-700 text-white',     hdr: 'bg-rose-50 border-rose-200',    btn: 'bg-rose-700 hover:bg-rose-800' },
  green:  { active: 'bg-green-700 text-white',    hdr: 'bg-green-50 border-green-200',  btn: 'bg-green-700 hover:bg-green-800' },
  indigo: { active: 'bg-indigo-700 text-white',   hdr: 'bg-indigo-50 border-indigo-200', btn: 'bg-indigo-700 hover:bg-indigo-800' },
};

function parseDate(val) {
  if (!val) return null;
  // DD.MM.YYYY or DD/MM/YYYY
  const m = val.match(/^(\d{1,2})[./](\d{1,2})[./](\d{4})$/);
  if (m) return { day: parseInt(m[1]), month: parseInt(m[2]), year: parseInt(m[3]) };
  // YYYY-MM-DD
  const m2 = val.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (m2) return { day: parseInt(m2[3]), month: parseInt(m2[2]), year: parseInt(m2[1]) };
  // DD-MM-YYYY
  const m3 = val.match(/^(\d{1,2})-(\d{1,2})-(\d{4})$/);
  if (m3) return { day: parseInt(m3[1]), month: parseInt(m3[2]), year: parseInt(m3[3]) };
  return null;
}

export default function VehicleDashboard() {
  const [activeTab, setActiveTab] = useState('doc');

  // Checklist filters
  const [clYear, setClYear]   = useState('');
  const [clMonth, setClMonth] = useState('');

  // KM Log filters
  const [kmYear, setKmYear]   = useState('');
  const [kmMonth, setKmMonth] = useState('');

  const tab = TABS.find(function(t) { return t.key === activeTab; });
  const tc = TAB_COLORS[tab ? tab.accent : 'stone'];

  // Checklist column filter: always show SR.NO, VEHICLE NAME, REMARK; filter others by year/month
  const checklistColumnFilter = function(colName) {
    const n = colName.toUpperCase().trim();
    if (n === 'SR.NO' || n === 'VEHICLE NAME' || n === 'REMARK') return true;
    if (!clYear && !clMonth) return true; // no filter active
    const yearMatch = clYear ? n.includes('-' + clYear + ' ') || n.endsWith('-' + clYear) : true;
    const monthMatch = clMonth ? n.startsWith(clMonth + '-') : true;
    return yearMatch && monthMatch;
  };

  // KM row filter: filter by year/month from DATE column
  const kmRowFilter = function(row, colByName) {
    if (!kmYear && !kmMonth) return true;
    const dateColId = colByName['DATE'];
    if (!dateColId) return true;
    const raw = (row.data && row.data[dateColId]) || '';
    if (!raw) return true; // keep blank rows visible
    const parsed = parseDate(raw);
    if (!parsed) return true;
    const yearOk = kmYear ? (parsed.year === parseInt(kmYear)) : true;
    const monthOk = kmMonth ? (parsed.month === parseInt(kmMonth)) : true;
    return yearOk && monthOk;
  };

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center gap-4 pb-2 border-b border-stone-200">
        <div className="w-12 h-12 rounded-2xl bg-stone-800 flex items-center justify-center shadow-lg">
          <Car className="w-6 h-6 text-white" />
        </div>
        <div>
          <h1 className="text-2xl font-black text-stone-900">Car & Vehicle Maintenance</h1>
          <p className="text-stone-500 text-sm">Santosh Bhalerao · Vehicle Management Dashboard</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 flex-wrap">
        {TABS.map(function(t) {
          const Icon = t.icon;
          const isActive = activeTab === t.key;
          return (
            <button key={t.key} onClick={function() { setActiveTab(t.key); }}
              data-testid={'tab-veh-' + t.key}
              className={'flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold transition-all shadow-sm ' + (isActive ? TAB_COLORS[t.accent].active + ' shadow-md' : 'bg-white text-stone-600 border border-stone-200 hover:bg-stone-50')}>
              <Icon className="w-4 h-4" />
              {t.label}
            </button>
          );
        })}
      </div>

      {/* Expiry Alert Legend (hide for diet/govt) */}
      {activeTab !== 'diet' && activeTab !== 'govt' && (
        <div data-testid="expiry-legend" className="flex items-center gap-3 flex-wrap text-xs bg-stone-50 border border-stone-200 rounded-xl px-4 py-2.5">
          <span className="font-bold text-stone-700 uppercase tracking-wide">Expiry alerts:</span>
          <span className="inline-flex items-center gap-1.5">
            <span className="w-3 h-3 rounded-sm bg-red-200 border border-red-300"></span>
            <span className="text-stone-600">Overdue</span>
          </span>
          <span className="inline-flex items-center gap-1.5">
            <span className="w-3 h-3 rounded-sm bg-red-100 border border-red-200"></span>
            <span className="text-stone-600">Within 7 days</span>
          </span>
          <span className="inline-flex items-center gap-1.5">
            <span className="w-3 h-3 rounded-sm bg-amber-100 border border-amber-200"></span>
            <span className="text-stone-600">Within 30 days</span>
          </span>
          <span className="ml-auto text-stone-400 italic">Auto-applied on Insurance / PUC / Fitness / Registration / Servicing dates</span>
        </div>
      )}

      {/* Year/Month filter bar for Checklist */}
      {activeTab === 'checklist' && (
        <div className="flex items-center gap-3 flex-wrap bg-blue-50 border border-blue-200 rounded-xl px-4 py-2.5">
          <CalendarDays className="w-4 h-4 text-blue-600 flex-shrink-0" />
          <span className="text-xs font-bold text-blue-700 uppercase tracking-wide">Filter columns:</span>
          <div className="flex items-center gap-2">
            <label className="text-xs text-blue-600 font-medium">Year</label>
            <select value={clYear} onChange={function(e) { setClYear(e.target.value); }}
              data-testid="checklist-year-filter"
              className="text-xs border border-blue-300 rounded-lg px-2 py-1.5 bg-white text-stone-700 focus:outline-none focus:ring-1 focus:ring-blue-400">
              <option value="">All Years</option>
              {YEARS_CHECKLIST.map(function(y) {
                return <option key={y} value={y}>20{y}</option>;
              })}
            </select>
          </div>
          <div className="flex items-center gap-2">
            <label className="text-xs text-blue-600 font-medium">Month</label>
            <select value={clMonth} onChange={function(e) { setClMonth(e.target.value); }}
              data-testid="checklist-month-filter"
              className="text-xs border border-blue-300 rounded-lg px-2 py-1.5 bg-white text-stone-700 focus:outline-none focus:ring-1 focus:ring-blue-400">
              <option value="">All Months</option>
              {MONTHS.map(function(m) {
                return <option key={m} value={m}>{m}</option>;
              })}
            </select>
          </div>
          {(clYear || clMonth) && (
            <button onClick={function() { setClYear(''); setClMonth(''); }}
              className="text-xs px-2 py-1 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 font-medium transition-colors">
              Clear
            </button>
          )}
          {(clYear || clMonth) && (
            <span className="text-xs text-blue-500 ml-auto italic">
              Showing: {clMonth || 'All months'} {clYear ? '20' + clYear : 'all years'} — SR.NO &amp; Vehicle Name always visible
            </span>
          )}
        </div>
      )}

      {/* Year/Month filter bar for KM Log */}
      {activeTab === 'km' && (
        <div className="flex items-center gap-3 flex-wrap bg-rose-50 border border-rose-200 rounded-xl px-4 py-2.5">
          <CalendarDays className="w-4 h-4 text-rose-600 flex-shrink-0" />
          <span className="text-xs font-bold text-rose-700 uppercase tracking-wide">Filter rows:</span>
          <div className="flex items-center gap-2">
            <label className="text-xs text-rose-600 font-medium">Year</label>
            <select value={kmYear} onChange={function(e) { setKmYear(e.target.value); }}
              data-testid="km-year-filter"
              className="text-xs border border-rose-300 rounded-lg px-2 py-1.5 bg-white text-stone-700 focus:outline-none focus:ring-1 focus:ring-rose-400">
              <option value="">All Years</option>
              {YEARS_KM.map(function(y) {
                return <option key={y} value={y}>{y}</option>;
              })}
            </select>
          </div>
          <div className="flex items-center gap-2">
            <label className="text-xs text-rose-600 font-medium">Month</label>
            <select value={kmMonth} onChange={function(e) { setKmMonth(e.target.value); }}
              data-testid="km-month-filter"
              className="text-xs border border-rose-300 rounded-lg px-2 py-1.5 bg-white text-stone-700 focus:outline-none focus:ring-1 focus:ring-rose-400">
              <option value="">All Months</option>
              {MONTHS.map(function(m, i) {
                return <option key={m} value={String(i + 1)}>{m}</option>;
              })}
            </select>
          </div>
          {(kmYear || kmMonth) && (
            <button onClick={function() { setKmYear(''); setKmMonth(''); }}
              className="text-xs px-2 py-1 bg-rose-100 text-rose-700 rounded-lg hover:bg-rose-200 font-medium transition-colors">
              Clear
            </button>
          )}
          {(kmYear || kmMonth) && (
            <span className="text-xs text-rose-500 ml-auto italic">
              Filtering by date: {kmMonth ? MONTHS[parseInt(kmMonth) - 1] : 'any month'} {kmYear || 'any year'}
            </span>
          )}
        </div>
      )}

      {/* Government Site Tab */}
      {activeTab === 'govt' && (
        <div className="bg-white rounded-2xl border border-indigo-200 shadow-sm overflow-hidden">
          <div className="px-5 py-3 border-b border-indigo-100 bg-indigo-50 flex items-center gap-2">
            <Globe className="w-4 h-4 text-indigo-600" />
            <p className="font-bold text-indigo-800 text-sm">Government Vehicle Information Portal</p>
            <span className="ml-auto text-xs text-indigo-400">External site — opens in new tab</span>
          </div>
          <div className="p-8 flex flex-col items-center gap-6">
            <div className="w-20 h-20 rounded-full bg-indigo-100 flex items-center justify-center shadow-inner">
              <Globe className="w-10 h-10 text-indigo-600" />
            </div>
            <div className="text-center max-w-md">
              <h2 className="text-xl font-bold text-stone-800 mb-2">Vehicle Info Portal</h2>
              <p className="text-stone-500 text-sm mb-1">Check vehicle registration details, owner information, insurance status, and more from the official government database.</p>
              <p className="text-xs text-stone-400 font-mono mt-2">vehicleinfo.app</p>
            </div>
            <a href="https://vehicleinfo.app" target="_blank" rel="noopener noreferrer"
              data-testid="govt-vehicle-link"
              className="flex items-center gap-2 px-8 py-3.5 bg-indigo-700 hover:bg-indigo-800 text-white rounded-xl font-semibold transition-colors shadow-md text-sm">
              <ExternalLink className="w-4 h-4" />
              Open Vehicle Info Site
            </a>
            <div className="grid grid-cols-3 gap-4 w-full max-w-lg mt-2">
              {[
                { label: 'RC Details', desc: 'Registration Certificate' },
                { label: 'Insurance', desc: 'Policy status & expiry' },
                { label: 'Challan', desc: 'Pending challans / fines' },
              ].map(function(item) {
                return (
                  <div key={item.label} className="bg-indigo-50 rounded-xl p-3 text-center border border-indigo-100">
                    <p className="text-xs font-bold text-indigo-700">{item.label}</p>
                    <p className="text-xs text-stone-500 mt-0.5">{item.desc}</p>
                  </div>
                );
              })}
            </div>
            <p className="text-xs text-stone-400 text-center">Enter any vehicle number on the site to get live details from the Vahan database.</p>
          </div>
        </div>
      )}

      {/* Sheet Content (all FinSheet-based tabs) */}
      {activeTab !== 'govt' && (
        <div className="bg-white rounded-2xl border border-stone-200 shadow-sm overflow-hidden">
          <div className={'px-5 py-3 border-b border-stone-100 ' + tc.hdr}>
            <p className="font-bold text-stone-800 text-sm">{tab ? tab.label : ''}</p>
          </div>

          {activeTab === 'doc' && (
            <FinSheet datasetKey="veh_doc_index" accent="stone" apiPrefix="/vehicle"
              numericCols={[]} enableExpiryAlerts={true} data-testid="sheet-doc" />
          )}
          {activeTab === 'vehicle' && (
            <FinSheet datasetKey="veh_vehicle_index" accent="emerald" apiPrefix="/vehicle"
              numericCols={['AVERAGE', 'METER']} enableExpiryAlerts={true} data-testid="sheet-vehicle" />
          )}
          {activeTab === 'checklist' && (
            <FinSheet datasetKey="veh_checklist" accent="blue" apiPrefix="/vehicle"
              numericCols={[]} enableExpiryAlerts={true}
              columnFilter={checklistColumnFilter}
              data-testid="sheet-checklist" />
          )}
          {activeTab === 'km' && (
            <FinSheet datasetKey="veh_km_log" accent="rose" apiPrefix="/vehicle"
              numericCols={['MORNING KM', 'NIGHT KM', 'TOTAL KM', 'FUEL AMOUNT']}
              enableExpiryAlerts={false}
              rowFilter={kmRowFilter}
              data-testid="sheet-km" />
          )}
          {activeTab === 'diet' && (
            <FinSheet datasetKey="veh_diet_chart" accent="green" apiPrefix="/vehicle"
              numericCols={[]} enableExpiryAlerts={false} enableStatusColors={true}
              defaultRowValues={{ DATE: new Date().toLocaleDateString('en-GB').replace(/\//g, '.') }}
              data-testid="sheet-diet" />
          )}
        </div>
      )}
    </div>
  );
}
